import { useCallback, useEffect, useState } from 'react';
import videosService from '../../services/videos';
import useInterval from '../Util/useInterval';

export type TranscodeProgress = {
  videoId: number;
  sessionId: number;
  sessionLabel?: string | null;
  sortOrder: number;
  url?: string | null;
  length?: string | null;
  state?: 'Running' | 'Succeeded' | 'Failed' | 'Copying' | null;
  backend?: 'Gcp' | 'Orchestrator' | null;
  jobId?: string | null;
  jobPageUrl?: string | null;
  jobState?: 'None' | 'Pending' | 'Running' | 'Succeeded' | 'Failed' | null;
  percent?: number | null;
  step?: string | null;
  pool?: string | null;
  pipeline?: string | null;
  retryCount: number;
  error?: string | null;
  createdAt?: number;
  startedAt?: number;
  finishedAt?: number;
};

const POLL_INTERVAL_MS = 5000;

const toMillis = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parse = (raw: any): TranscodeProgress => ({
  ...raw,
  createdAt: toMillis(raw?.createdAt),
  startedAt: toMillis(raw?.startedAt),
  finishedAt: toMillis(raw?.finishedAt),
});

export const isTranscodeBusy = (item: TranscodeProgress) => item.state === 'Running' || item.state === 'Copying';

const useTranscodeStatus = (sessionId?: string, enabled = true) => {
  const [data, setData] = useState<TranscodeProgress[] | null>(null);
  const [error, setError] = useState(false);
  const busy = !data || data.some(isTranscodeBusy);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = sessionId
        ? await videosService.getSessionTranscodeStatus(sessionId)
        : await videosService.getBusyTranscodes();
      setData((Array.isArray(res?.items) ? res.items : []).map(parse));
      setError(false);
    } catch (err) {
      console.error('Failed to fetch transcode status', err);
      setError(true);
    }
  }, [sessionId, enabled]);

  useEffect(() => {
    setData(null);
    setError(false);
    load();
  }, [load]);
  useInterval(load, enabled && (busy || !sessionId) ? POLL_INTERVAL_MS : null);

  return { data, error, reload: load };
};

export default useTranscodeStatus;
